import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Anchor, CheckCircle2, AlertTriangle } from "lucide-react";

const ROLE_DESC = {
    viewer: "Read-only access — see entries but can't add or edit.",
    editor: "Full add / edit / delete on entries — can't change vessel settings.",
    admin: "Edit + manage other co-captains.",
};

/**
 * /invite/:token — landing page when a co-captain clicks "Accept" in their email.
 *
 * Flow:
 *   1. Look up invite by token (public endpoint, no auth required to preview)
 *   2. If user is not logged in, prompt them to sign in / sign up with the
 *      invited email address
 *   3. If logged-in user matches the invited email → "Accept" button → POST,
 *      then redirect to the vessel
 *   4. If logged-in user is a DIFFERENT email → ask them to sign out and back
 *      in with the invited email
 */
export default function InviteAccept() {
    const { token } = useParams();
    const { user, loading } = useAuth();
    const nav = useNavigate();
    const [inv, setInv] = useState(null);
    const [err, setErr] = useState("");
    const [accepting, setAccepting] = useState(false);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const { data } = await api.get(`/invitations/${token}`);
                if (!cancelled) setInv(data);
            } catch (e) {
                if (!cancelled) setErr(formatApiErrorDetail(e.response?.data?.detail) || "This invitation isn't valid anymore.");
            }
        })();
        return () => { cancelled = true; };
    }, [token]);

    const accept = async () => {
        setAccepting(true);
        try {
            const { data } = await api.post(`/invitations/${token}/accept`);
            nav(`/app/${data.vessel_id}`, { replace: true });
        } catch (e) {
            setErr(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't accept the invitation.");
            setAccepting(false);
        }
    };

    if (loading || (!inv && !err)) {
        return <Shell><div style={{ color: "var(--bm-text-muted)" }}>Looking up your invitation…</div></Shell>;
    }
    if (err) {
        return (
            <Shell>
                <AlertTriangle size={36} color="var(--bm-bad)" style={{ marginBottom: 12 }} />
                <h1 className="bm-serif" style={{ fontSize: 24, fontWeight: 500, margin: "0 0 10px" }}>Invitation problem</h1>
                <p style={{ color: "var(--bm-text-muted)", margin: "0 0 20px" }}>{err}</p>
                <Link to="/" className="bm-btn bm-btn-ghost" data-testid="invite-home-link">Back to home</Link>
            </Shell>
        );
    }
    const expiresInDays = Math.ceil((new Date(inv.expires_at).getTime() - Date.now()) / 86400000);
    const emailMismatch = user && user.email && user.email.toLowerCase() !== inv.email.toLowerCase();

    return (
        <Shell>
            <div style={{ fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--bm-gold)", marginBottom: 10 }}>
                Crew invitation
            </div>
            <h1 className="bm-serif" data-testid="invite-vessel-name" style={{ fontSize: 30, fontWeight: 500, margin: "0 0 14px" }}>
                {inv.vessel_name}
            </h1>
            <p style={{ color: "var(--bm-text-muted)", margin: "0 0 6px" }}>
                <strong style={{ color: "var(--bm-text)" }}>{inv.inviter_name}</strong> has invited <span data-testid="invite-email">{inv.email}</span> to crew their vessel.
            </p>
            <div style={{ background: "rgba(212,181,114,0.07)", border: "1px solid var(--bm-border-soft)", borderRadius: 10, padding: 16, margin: "20px 0" }}>
                <div style={{ fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--bm-gold)", marginBottom: 4 }}>Role · {inv.role}</div>
                <div style={{ color: "var(--bm-text)", fontSize: 14 }}>{ROLE_DESC[inv.role] || "Co-captain access."}</div>
            </div>
            <div style={{ fontSize: 12, color: "var(--bm-text-muted)", marginBottom: 22 }}>
                Expires in {expiresInDays} day{expiresInDays === 1 ? "" : "s"}.
            </div>

            {!user ? (
                <>
                    <p style={{ color: "var(--bm-text-muted)", marginBottom: 14 }}>You need a Bosun's Mate account to accept. Sign in or create one with the email <strong style={{ color: "var(--bm-text)" }}>{inv.email}</strong>:</p>
                    <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                        <Link
                            to={`/login?next=/invite/${token}&email=${encodeURIComponent(inv.email)}`}
                            className="bm-btn bm-btn-gold"
                            data-testid="invite-login-btn"
                        >Sign in</Link>
                        <Link
                            to={`/register?next=/invite/${token}&email=${encodeURIComponent(inv.email)}`}
                            className="bm-btn bm-btn-ghost"
                            data-testid="invite-register-btn"
                        >Create account</Link>
                    </div>
                </>
            ) : emailMismatch ? (
                <>
                    <p style={{ color: "var(--bm-text-muted)", marginBottom: 14 }}>
                        You're signed in as <strong style={{ color: "var(--bm-text)" }}>{user.email}</strong>, but this invite is for <strong style={{ color: "var(--bm-text)" }}>{inv.email}</strong>.
                    </p>
                    <p style={{ color: "var(--bm-text-muted)", fontSize: 13 }}>Sign out and sign back in with that email to accept.</p>
                </>
            ) : (
                <button
                    className="bm-btn bm-btn-gold"
                    data-testid="invite-accept-btn"
                    onClick={accept}
                    disabled={accepting}
                    style={{ padding: "12px 24px", fontSize: 14 }}
                >
                    {accepting ? "Accepting…" : (<><CheckCircle2 size={16} /> Accept &amp; join crew</>)}
                </button>
            )}
        </Shell>
    );
}

function Shell({ children }) {
    return (
        <div data-testid="invite-accept-page" style={{ minHeight: "100vh", background: "var(--bm-bg)", display: "grid", placeItems: "center", padding: 24 }}>
            <div style={{ maxWidth: 480, width: "100%" }}>
                <Link to="/" style={{ display: "inline-flex", alignItems: "center", gap: 10, textDecoration: "none", color: "inherit", marginBottom: 26 }}>
                    <Anchor size={20} color="var(--bm-gold)" />
                    <span className="bm-display" style={{ fontSize: 22 }}>Bosun's Mate</span>
                </Link>
                <div className="bm-card" style={{ padding: 30 }}>
                    {children}
                </div>
            </div>
        </div>
    );
}
